import Plausible from "plausible-tracker";

export const { enableAutoPageviews, trackEvent: plausibleTrackEvent } = Plausible({
  domain: import.meta.env.VITE_PUBLIC_DOMAIN,
  apiHost: "https://analytics.webtie.dev"
});

let posthog: any = null;
const isPostHogEnabled = import.meta.env.VITE_POSTHOG_DISABLED !== "true";

if (isPostHogEnabled && import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
  import("posthog-js")
    .then((posthogModule) => {
      posthog = posthogModule.default;
      posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
        api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
        capture_exceptions: true,
        debug: import.meta.env.VITE_POSTHOG_DEBUG === "true"
      });
    })
    .catch(() => {
      // PostHog failed to load, continue without it
      console.warn("PostHog failed to load");
    });
}

/**
 * Track a custom event. This is the main function that was in the original analytics.tsx
 * @param event - Name of the event
 * @param properties - Optional properties object
 */
export const trackEvent = (event: string, properties?: Record<string, any>): void => {
  plausibleTrackEvent(event, { props: properties });

  if (posthog && posthog.__loaded) {
    posthog.capture(event, properties);
  }
};

/**
 * Track form events (start, complete, error)
 */
export const trackFormEvent = (action: string, formName: string, properties?: Record<string, any>): void => {
  trackEvent(`form_${action}`, {
    form_name: formName,
    ...properties
  });
};

/**
 * Track registration-related events
 */
export const trackRegistrationEvent = (action: string, properties?: Record<string, any>): void => {
  trackEvent(`registration_${action}`, properties);
};

/**
 * Track feature usage events
 */
export const trackFeatureUsage = (feature: string, properties?: Record<string, any>): void => {
  trackEvent("feature_used", {
    feature_name: feature,
    ...properties
  });
};

/**
 * Identify a user for analytics
 */
export const identifyUser = (userId: string, properties?: Record<string, any>): void => {
  // Track user identification in PostHog if available
  if (posthog && posthog.__loaded) {
    posthog.identify(userId, properties);
  }

  // For Plausible, we can track this as a custom event
  trackEvent("user_identified", {
    user_id: userId,
    ...properties
  });
};

/**
 * Reset user identity (on logout)
 */
export const resetUser = (): void => {
  // Reset user in PostHog if available
  if (posthog && posthog.__loaded) {
    posthog.reset();
  }

  // Track logout event
  trackEvent("user_logout");
};
